/* eslint no-param-reassign: 0 */
import { DirectoryGroupingConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { cloneDeep, isEqual } from 'lodash';
import Vue from 'vue';

const { memberTypes } = DirectoryGroupingConsts;

/**
 * This method handles a special case where the SystemUserCollection should be queried for items
 * to bind or unbind when the targetType is "user". But because there is already a UserCollection
 * used for administrators, named as such because it uses the /api/users endpoint, this special case
 * can't be handled in the StoreModuleMap itself. And it makes more sense to do it here since admin
 * users are not bound/unbound to other items.
 */
export const getStoreModuleType = (targetType) => {
  if (targetType === memberTypes.user) {
    return ItemTypeConsts.systemUser;
  }

  return targetType;
};

/**
 * @description This factory function returns a new object that should be used as a Vuex module for
 * the model of a component. It is meant to replace the ModelMixin object that is
 * currently used for this purpose.
 * @param getDefaultItem A function that returns the default item used as the model.
 * @param idField The id field used for the model, e.g. 'id', or '_id'.
 * @param api An api instance that is used when making requests to the server to perform CRUD
 * operations. This instance should be derived from the CollectionApi.
 * @returns A new singleton object suitable for use as a Vuex module intended for use in a
 * component.
 * @todo Should this function inspect the parameters and throw an exception if they are invalid?
 */
export default function createModel(getDefaultItem, idField, api) {
  return {
    namespaced: true,

    state: {
      item: getDefaultItem(),

      // Explicitly determine whether an item is being created or updated. This
      // is useful so that the UI can determine the _precise_ state of an item.
      itemState: {
        create: undefined,
        update: undefined,
      },

      // Keep track of initial item state so the Aside Panel can be "stateful"
      previousItem: getDefaultItem(),
    },

    actions: {
      bindAssociations(context, { parentType, targetType, confirm = false }) {
        const { dispatch, getters, rootGetters } = context;
        const selectedTargetIds = getters.getTargetsToBind(targetType);
        const unselectedTargetIds = getters.getTargetsToUnbind(targetType);

        const directoryGroupingPayload = {
          parentType,
          parentId: getters.id,
          targetType,
          selectedTargetIds,
          unselectedTargetIds,
        };

        const action = confirm ? 'confirmAndBindAssociations' : 'bindAssociations';

        if (confirm) {
          const type = getStoreModuleType(targetType);
          const module = StoreModuleMap[type];
          const getItem = id => rootGetters[`${module}/getItem`](id);

          directoryGroupingPayload.selectedItems = selectedTargetIds.map(id => getItem(id));
          directoryGroupingPayload.unselectedItems = unselectedTargetIds.map(id => getItem(id));
        }

        return dispatch(
          `DirectoryGroupingModule/${action}`,
          directoryGroupingPayload,
          { root: true },
        );
      },

      confirmAndBindAssociations({ dispatch }, { parentType, targetType }) {
        return dispatch('bindAssociations', { parentType, targetType, confirm: true });
      },

      fetchItem(context = { getters: { id: {} } }) {
        const { id } = context.getters;

        if (id) {
          return api
            .get(id)
            .then((item) => {
              // Clone the item so that any changes to non-primitive type values,
              // such as an array of phone numbers, are _not_ accidentally made
              // to the previous item when the item is mutated.
              context.commit('SET_ITEM', cloneDeep(item));
              context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));

              return item;
            });
        }

        return Promise.resolve();
      },

      resetItem({ commit }) {
        commit('RESET_ITEM');
      },

      saveItem(context) {
        const { getters = {} } = context;

        const handleSaveItem = (item) => {
          context.commit('SET_ITEM', cloneDeep(item));
          context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
          context.commit('SET_ITEM_STATE', { update: true });
          return item;
        };

        // eslint-disable-next-line prefer-destructuring
        const item = getters.item;

        return getters.isItemBeingCreated
          ? api.create(item).then(handleSaveItem)
          : api.update(item).then(handleSaveItem);
      },

      setItemField({ commit }, { key, value }) {
        commit('SET_ITEM', { [key]: value });
      },

      setItemStateToCreate({ commit }) {
        commit('SET_ITEM_STATE', { create: true });
      },

      setItemStateToUpdate({ commit }) {
        commit('SET_ITEM_STATE', { update: true });
      },

      setPreviousItemField({ commit }, { key, value }) {
        commit('SET_PREVIOUS_ITEM', { [key]: value });
      },

      updateItem({ commit }, item) {
        commit('SET_ITEM', item);
      },

      updatePreviousItem({ commit }, item) {
        commit('SET_PREVIOUS_ITEM', item);
      },

      unset({ commit }, payload) {
        commit('UNSET', payload);
      },
    },

    getters: {
      getTargetsToBind(state, getters, rootState, rootGetters) {
        return (targetType) => {
          const type = getStoreModuleType(targetType);
          const module = StoreModuleMap[type];
          return rootGetters[`${module}/getItemsToBind`].map(item => item.id);
        };
      },

      getTargetsToUnbind(state, getters, rootState, rootGetters) {
        return (targetType) => {
          const type = getStoreModuleType(targetType);
          const module = StoreModuleMap[type];
          return rootGetters[`${module}/getItemsToUnbind`].map(item => item.id);
        };
      },

      haveAssociationsChanged(state, getters) {
        return (targetTypes = []) => {
          const targetTypesToBindCount = targetTypes
            .reduce((acc, targetType) => acc + getters.getTargetsToBind(targetType).length, 0);

          const targetTypesToUnbindCount = targetTypes
            .reduce((acc, targetType) => acc + getters.getTargetsToUnbind(targetType).length, 0);

          return targetTypesToBindCount > 0 || targetTypesToUnbindCount > 0;
        };
      },

      /**
       * JSON.parse(JSON.stringify()) is used here to to convert the Vue objects
       * to regular JSON objects and remove Vue's observers. Observers in nested
       * objects in the state, such as on a phone number object in an array of
       * phone numbers, will incorrectly return false when compared with lodash's
       * isEqual() method, even if when evaluated they both have the same values.
       * @see https://github.com/vuejs/Discussion/issues/292#issuecomment-122825813
       * @see https://www.reddit.com/r/vuejs/comments/5p96hy/what_really_is_object_ob_observer/
       */
      hasItemChanged(state = { item: {}, previousItem: {} }) {
        const item = JSON.parse(JSON.stringify(state.item));
        const previousItem = JSON.parse(JSON.stringify(state.previousItem));
        return !isEqual(item, previousItem);
      },

      id(state = { item: {} }) {
        return state.item[idField];
      },

      idField() {
        return idField;
      },

      isItemBeingCreated(state = { itemState: {} }) {
        return state.itemState.create === true;
      },

      isItemBeingUpdated(state = { itemState: {} }) {
        return state.itemState.update === true;
      },

      item(state = { item: {} }) {
        return state.item;
      },

      previousItem(state = { previousItem: {} }) {
        return state.previousItem;
      },
    },

    mutations: {
      RESET_ITEM(state = { itemState: {} }) {
        state.item = getDefaultItem();
        state.itemState.create = undefined;
        state.itemState.update = undefined;
        state.previousItem = getDefaultItem();
      },

      /**
       * Note that Vue.set() is used here in case the key doesn't yet exist in
       * the default state!
       */
      SET_ITEM(state = { item: {} }, item = {}) {
        // TODO: Is there a better way to set state.item from _all_ the fields in the item?
        Object.keys(item).forEach(key => Vue.set(state.item, key, item[key]));
      },

      SET_ITEM_STATE(state = { itemState: {} }, { create, update }) {
        state.itemState.create = create;
        state.itemState.update = update;
      },

      SET_PREVIOUS_ITEM(state = { previousItem: {} }, item = {}) {
        // TODO: Is there a better way to set state.item from _all_ the fields in the item?
        Object.keys(item).forEach(key => Vue.set(state.previousItem, key, item[key]));
      },

      UNSET(state = { item: {} }, { key, keys = [] }) {
        if (key) {
          Vue.delete(state.item, key);
        }

        keys.map(k => Vue.delete(state.item, k));
      },
    },
  };
}
